import { IconProp, SizeProp } from '@fortawesome/fontawesome-svg-core';
import React from 'react';
import styled from 'styled-components';

import { FontSize } from '../theme/FontSize';
import { Rounded } from '../theme/Rounded';
import { Spacing } from '../theme/Spacing';
import { ThemeType } from '../theme/Theme';
import { AtiraIcon } from './AtiraIcon';
import { Flex } from './Flex';
import { Spinner } from './Spinner';

interface StyledButtonProps {
  color?: keyof ThemeType;
  backgroundColor?: keyof ThemeType;
  border?: string;
  align?: string;
  fontSize?: string;
  width?: React.CSSProperties['width'];
  textAlign?: 'start' | 'center' | 'end';
  padding?: string;
  borderRadius?: keyof typeof Rounded;
  height?: string;
  margin?: string;
  hover?: ButtonProps['hover'];
  hoverColor?: ButtonProps['hoverColor'];
  hoverBackgroundColor?: ButtonProps['hoverBackgroundColor'];
  dataTestId?: string;
}

// @ts-ignore
const StyledButton = styled.button.attrs((props: StyledButtonProps) => ({
  'data-testid': props.dataTestId,
}))<StyledButtonProps>`
  color: ${(props) =>
    props.color ? props.theme[props.color] : props.theme['onMain']};
  background-color: ${(props) =>
    props.backgroundColor
      ? props.theme[props.backgroundColor]
      : props.theme['main']};
  padding: ${(props) => props.padding || `${Spacing.s} ${Spacing.l}`};
  border-radius: ${(props) =>
    props.borderRadius ? Rounded[props.borderRadius] : Rounded.md};
  border: ${(props) => props.border || 'none'};
  border-radius: ${(props) => props.borderRadius || Rounded.md};
  margin: ${(props) => props.margin || '0'};
  text-align: ${(props) => props.align};
  font-size: ${(props) => props.fontSize || FontSize.m};
  width: ${(props) => props.width};
  min-width: ${(props) => props.width};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  text-align: ${(props) => props.textAlign};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  height: ${(props) => props.height};
  transition: all 0.3s;
  font-family: inherit;
  line-height: normal;

  div > span {
    color: inherit !important;
  }

  ${({
    hover,
    theme,
    hoverColor,
    hoverBackgroundColor,
    backgroundColor,
    color,
  }) =>
    hover &&
    `&:hover { background-color: ${hoverBackgroundColor ? theme[hoverBackgroundColor || 'mainHover'] : backgroundColor};
      color: ${hoverColor ? theme[hoverColor || 'onMain'] : color}
    }`};
`;

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: IconProp;
  color?: keyof ThemeType;
  backgroundColor?: keyof ThemeType;
  onClick?: (e?: React.MouseEvent) => void;
  children?: React.ReactNode;
  title?: string;
  border?: string;
  align?: string;
  fontSize?: string;
  width?: React.CSSProperties['width'];
  height?: string;
  textAlign?: StyledButtonProps['textAlign'];
  padding?: string;
  borderRadius?: keyof typeof Rounded;
  disabled?: boolean;
  loading?: boolean;
  margin?: string;
  style?: React.CSSProperties;
  hover?: boolean;
  hoverColor?: keyof ThemeType;
  iconWidth?: SizeProp;
  iconColor?: keyof ThemeType;
  hoverBackgroundColor?: keyof ThemeType;
  dataTestId?: string;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  iconColor,
  iconWidth,
  loading,
  dataTestId,
  ...props
}) => {
  const localOnClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (props.disabled || loading) {
      return;
    }

    props.onClick?.(e);
  };

  if (loading) {
    return (
      <StyledButton {...props} data-testId={dataTestId} onClick={localOnClick}>
        <Spinner />
      </StyledButton>
    );
  }

  if (props.title) {
    return (
      <StyledButton {...props} data-testId={dataTestId} onClick={localOnClick}>
        {props.title}
      </StyledButton>
    );
  }

  if (props.icon) {
    return (
      <StyledButton {...props} data-testId={dataTestId} onClick={localOnClick}>
        <Flex
          gap={children ? 's' : 'o'}
          justifyContent="center"
          alignItems="center"
        >
          <AtiraIcon icon={props.icon} size={iconWidth} color={iconColor} />

          <span>{children || props.title}</span>
        </Flex>
      </StyledButton>
    );
  }

  return (
    <StyledButton {...props} data-testId={dataTestId} onClick={localOnClick}>
      {children}
    </StyledButton>
  );
};
